import type { Note, NoteCreate, NoteUpdate } from '@/shared/types'
import { API_URL, createHeaders } from './base'

interface GetNotesParams {
  tag?: string
  sort_by?: string
  sort_order?: string
}

export async function getNotes(params?: GetNotesParams): Promise<Note[]> {
  const searchParams = new URLSearchParams()
  if (params?.tag) searchParams.set('tag', params.tag)
  if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
  if (params?.sort_order) searchParams.set('sort_order', params.sort_order)

  const url = `${API_URL}/notes${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
  const response = await fetch(url, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch notes')
  return response.json()
}

export async function getNote(id: number): Promise<Note> {
  const response = await fetch(`${API_URL}/notes/${id}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch note')
  return response.json()
}

export async function createNote(note: NoteCreate): Promise<Note> {
  const response = await fetch(`${API_URL}/notes`, {
    method: 'POST',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(note),
  })
  if (!response.ok) throw new Error('Failed to create note')
  return response.json()
}

export async function updateNote(id: number, updates: NoteUpdate): Promise<Note> {
  const response = await fetch(`${API_URL}/notes/${id}`, {
    method: 'PUT',
    headers: createHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates),
  })
  if (!response.ok) throw new Error('Failed to update note')
  return response.json()
}

export async function deleteNote(id: number): Promise<void> {
  const response = await fetch(`${API_URL}/notes/${id}`, {
    method: 'DELETE',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to delete note')
}

export async function searchNotes(query: string): Promise<Note[]> {
  const response = await fetch(`${API_URL}/notes/search?q=${encodeURIComponent(query)}`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to search notes')
  return response.json()
}

export async function getPinnedNotes(): Promise<Note[]> {
  const response = await fetch(`${API_URL}/notes/pinned`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch pinned notes')
  return response.json()
}

export async function getArchivedNotes(): Promise<Note[]> {
  const response = await fetch(`${API_URL}/notes/archived`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch archived notes')
  return response.json()
}

export async function getAllTags(): Promise<string[]> {
  const response = await fetch(`${API_URL}/notes/tags`, {
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch tags')
  return response.json()
}

export async function togglePin(id: number): Promise<Note> {
  const response = await fetch(`${API_URL}/notes/${id}/pin`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to toggle pin')
  return response.json()
}

export async function toggleArchive(id: number): Promise<Note> {
  const response = await fetch(`${API_URL}/notes/${id}/archive`, {
    method: 'POST',
    headers: createHeaders()
  })
  if (!response.ok) throw new Error('Failed to toggle archive')
  return response.json()
}
